/**
 * Dropdown for toggling the session's active roles on and off.
 * The base 'user' role is always enabled (server-side enforced).
 */

import { useFetcher } from '@remix-run/react'
import { ChevronDown, Shield } from 'lucide-react'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from './ui/dropdown-menu'

export type RoleSwitcherProps = {
	roles: { name: string }[]
	disabledRoles: string[]
}

const roleLabels: Record<string, string> = {
	admin: 'Admin',
	coach: 'Coach',
	user: 'User',
}

export function RoleSwitcher({ roles, disabledRoles }: RoleSwitcherProps) {
	const fetcher = useFetcher()
	const isSwitching = fetcher.state !== 'idle'

	const toggleableRoles = roles.filter((role) => role.name !== 'user')

	// Only show for accounts with elevated roles
	if (toggleableRoles.length === 0) {
		return null
	}

	const enabledNames = [
		'user',
		...toggleableRoles
			.filter((role) => !disabledRoles.includes(role.name))
			.map((role) => role.name),
	]
	const triggerLabel =
		disabledRoles.length === 0
			? 'All Roles'
			: enabledNames.map((name) => roleLabels[name] ?? name).join(' + ')

	const toggleRole = (roleName: string, enabled: boolean) => {
		fetcher.submit(
			{ intent: 'toggle-role', roleName, enabled: String(enabled) },
			{ method: 'post', action: '/api/role-switch' },
		)
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className="gap-1"
					disabled={isSwitching}
				>
					<Shield className="h-4 w-4" />
					<span className="hidden sm:inline">{triggerLabel}</span>
					<ChevronDown className="h-3 w-3" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<DropdownMenuLabel>Active Roles</DropdownMenuLabel>
				<DropdownMenuSeparator />

				<DropdownMenuCheckboxItem checked disabled>
					{roleLabels.user}
				</DropdownMenuCheckboxItem>

				{toggleableRoles.map((role) => {
					const enabled = !disabledRoles.includes(role.name)
					return (
						<DropdownMenuCheckboxItem
							key={role.name}
							checked={enabled}
							// keep the menu open so several roles can be toggled in a row
							onSelect={(event) => event.preventDefault()}
							onCheckedChange={(checked) =>
								toggleRole(role.name, checked === true)
							}
						>
							{roleLabels[role.name] ?? role.name}
						</DropdownMenuCheckboxItem>
					)
				})}

				<DropdownMenuSeparator />
				<DropdownMenuItem
					disabled={disabledRoles.length === 0}
					onSelect={() => {
						fetcher.submit(
							{ intent: 'clear-role' },
							{ method: 'post', action: '/api/role-switch' },
						)
					}}
				>
					Enable all roles
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
